import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';

import { EstateForm } from '@/features/estates/components/estate-form';
import { type EstateSchemaType, getCreateEstateSchema } from '@/features/estates/schemas/estate';
import { useUpdateProjectBasicData } from '@/features/projects/api/project-details';
import { getProjectQueryOptions, type Project } from '@/features/projects/api/projects';

import { ModalBase } from './modals-base';

export type EditProjectModalProps = {
  project: Project & { tenantId?: string };
};

export const EditProjectModal: React.FC<EditProjectModalProps> = ({ project }) => {
  const schema = getCreateEstateSchema();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const router = useRouter();

  const { mutateAsync: editProjectAsync } = useUpdateProjectBasicData({
    mutationConfig: {
      onSuccess: ([project]) => {
        toast.success(t('update-project-success'));
        queryClient.invalidateQueries({ queryKey: getProjectQueryOptions(project?.id).queryKey });
        router.invalidate();
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  });

  const methods = useForm<EstateSchemaType>({
    resolver: zodResolver(schema),
    defaultValues: {
      estateData: {
        name: '',
        propertyType: '',
        marketType: '',
        tenantId: '',
      },
      addressData: {
        address: '',
        postalCode: '',
        city: '',
        countryId: '',
      },
    },
  });

  const { reset } = methods;

  useEffect(() => {
    reset((prev) => ({
      ...prev,
      estateData: {
        ...prev.estateData,
        name: project.name ?? '',
        marketType: project.details?.marketType ?? '',
        propertyType: project.details?.propertyType ?? '',
        propertyLevel: 'project',
        tenantId: project.tenant?.id ?? '',
      },
      addressData: {
        ...prev.addressData,
        ...(project?.address?.address && {
          addressAutoComplete: project.address.address,
          address: project.address.address,
        }),
        postalCode: project.address?.postalCode ?? '',
        city: project.address?.city ?? '',
        countryId: project.address?.country?.id || '',
        ...(project.address?.long && { long: Number(project.address?.long) }),
        ...(project?.address?.street && { street: project.address.street }),
        ...(project?.address?.houseNumber && {
          houseNumber: project.address.houseNumber,
        }),
      },
    }));
  }, [reset, project]);

  const onSubmit = async (args: EstateSchemaType) => {
    editProjectAsync({
      id: project.id,
      projectData: args.estateData,
      addressData: args.addressData,
    });
  };

  return (
    <ModalBase>
      <FormProvider {...methods}>
        <EstateForm label={t('edit-project')} onSubmit={onSubmit} />
      </FormProvider>
    </ModalBase>
  );
};
